// Spår S1 — Pure subscription-split-plan builder.
//
// "Redigera abonnemang" semantics per spec D11 (operator-locked):
//   - "Edit" must take effect from a chosen DATE forward (default today)
//   - Historic instances MUST stay intact (no retro mutation)
//   - The subscription is UNBROKEN across the split boundary — old values
//     apply UP TO (and excluding) the split date; new values apply FROM
//     the split date onwards
//
// Strategy A — split-pattern (operator-locked after exhaustive diagnosis):
//   1. UPDATE old row: valid_until = splitDate - 1
//   2. INSERT new row: copy all fields + apply edits + valid_from = splitDate +
//      valid_until = old.valid_until (preserves end-date by default)
//   3. Migrate future-dated subscription_releases (release_date >= splitDate
//      AND status='released') from old.id → new.id (preserves customer credit)
//   4. Migrate future-dated subscription_exceptions (original_date >= splitDate)
//      from old.id → new.id
//   5. Split skipped_dates around splitDate: < splitDate stays on old,
//      >= splitDate carries to new
//
// Beslut C (operator-locked, 2026-05-18): split-datum = edits.valid_from
// (operator's chosen field, default today, editable forward). NOT hardcoded
// to today. The RPC parameter uses this value; old.valid_until is the day
// before. Subscription is unbroken across the boundary.
//
// This module is PURE — no Supabase client, no React, no Date.now(). It
// builds a PLAN; the caller (SubscriptionDetailDialog.handleSave) executes
// the plan via a single SECURITY DEFINER RPC. Dry-run mode = build plan +
// log, skip RPC call.

// ─── Types ──────────────────────────────────────────────────────────────

export interface SubscriptionRow {
  id: string;
  organization_id: string;
  user_id: string | null;
  customer_id: string | null;
  court_id: string;
  day_of_week: number;
  start_time: string;        // "HH:mm:ss"
  end_time: string;          // "HH:mm:ss"
  valid_from: string;        // "YYYY-MM-DD"
  valid_until: string;       // "YYYY-MM-DD"
  status: string;            // 'active' | 'paused' | 'cancelled'
  skipped_dates: string[] | null;
  price: number | null;
  price_minor: number | null;
  currency: string;
  interval_weeks: number | null;
  comment: string | null;
  payment_method: string | null;
  payment_method_id: string | null;
  created_by: string | null;
}

export interface SubscriptionEdits {
  court_id?: string;
  day_of_week?: number;
  start_time?: string;       // "HH:mm:ss" (caller normalizes "HH:mm" → "HH:mm:ss")
  end_time?: string;
  valid_from?: string;       // operator-selected split-date (Beslut C)
  valid_until?: string;
  interval_weeks?: number;
  price?: number;
  payment_method?: string;
  comment?: string | null;
}

// S1-fix (D12) — two write-modes:
//   - 'inplace' when splitDate === oldRow.valid_from (no historical period
//     to protect; UPDATE existing row, same id, no FK migration)
//   - 'split' when splitDate > oldRow.valid_from (existing split-pattern:
//     shrink old + INSERT new + migrate future-dated FK rows)
// The RPC decides which branch internally based on the same condition;
// the plan-helper computes the mode so tests + audit-log metadata can
// surface the distinction without re-querying.

export type SubscriptionSplitPlan =
  | { noop: true; reason: "no_changes" }
  | {
      noop: false;
      mode: "inplace";
      /** Same as oldRow.valid_from (= edits.valid_from) — surfaced for symmetry */
      splitDate: string;
      /** Fields to UPDATE on the existing row (id preserved). */
      update: {
        id: string;                         // same as oldRow.id
        court_id: string;
        day_of_week: number;
        start_time: string;
        end_time: string;
        valid_until: string;                // operator-edited (may equal old)
        interval_weeks: number | null;
        price: number | null;
        price_minor: number | null;
        payment_method: string | null;
        comment: string | null;
      };
    }
  | {
      noop: false;
      mode: "split";
      /** Date the split happens — operator's edits.valid_from > oldRow.valid_from.
       *  ISO "YYYY-MM-DD". */
      splitDate: string;
      oldUpdate: {
        id: string;
        valid_until: string;       // splitDate - 1 (ISO)
        skipped_dates: string[];   // dates < splitDate
      };
      /** Fields to INSERT as the new row. id + created_at NOT included
       *  (DB generates). Caller passes these to the RPC. */
      newInsert: Omit<SubscriptionRow, "id">;
      /** Future-dated subscription_releases migrate from old.id → new.id.
       *  Filter spec — caller's RPC applies this WHERE clause. */
      releaseMigration: {
        oldId: string;
        filterCondition: "release_date >= splitDate AND status='released'";
      };
      /** Future-dated subscription_exceptions migrate from old.id → new.id. */
      exceptionMigration: {
        oldId: string;
        filterCondition: "original_date >= splitDate";
      };
      /** Skipped-dates split for documentation; oldRetain + newCarry both
       *  reflected in oldUpdate.skipped_dates + newInsert.skipped_dates. */
      skippedDatesSplit: {
        oldRetain: string[];        // dates < splitDate
        newCarry: string[];         // dates >= splitDate
      };
    };

// ─── Builder ────────────────────────────────────────────────────────────

/**
 * Build a SubscriptionSplitPlan from the current subscription row + the
 * operator's edits. The plan is what to write; the caller executes it.
 *
 * Inputs:
 *   - oldRow: the current `recurring_bookings` row (full shape)
 *   - edits:  fields the operator wants to change. Undefined = unchanged.
 *             `edits.valid_from` is the OPERATOR-SELECTED SPLIT DATE
 *             (Beslut C — default today, editable forward; UI prefills).
 *   - today:  ISO "YYYY-MM-DD" — injected for testability. Used as the
 *             fallback split-date IFF edits.valid_from is absent (which
 *             shouldn't happen via the UI — the field is always pre-filled).
 *
 * Returns:
 *   - { noop: true, reason: 'no_changes' } when zero fields differ
 *   - { noop: false, ...plan } when at least one field differs
 */
export function buildSubscriptionSplitPlan(args: {
  oldRow: SubscriptionRow;
  edits: SubscriptionEdits;
  today: string;
}): SubscriptionSplitPlan {
  const { oldRow, edits, today } = args;

  // No-op detection — compare every editable field, treating undefined as
  // "unchanged". Comment normalizes "" → null to avoid false-positive diff
  // when operator's empty textarea ≡ DB null.
  const commentNormalized = (v: string | null | undefined): string | null => {
    if (v === undefined) return undefined as unknown as null; // sentinel
    if (v === null) return null;
    return v.length === 0 ? null : v;
  };

  const hasChange =
    (edits.court_id        !== undefined && edits.court_id        !== oldRow.court_id) ||
    (edits.day_of_week     !== undefined && edits.day_of_week     !== oldRow.day_of_week) ||
    (edits.start_time      !== undefined && edits.start_time      !== oldRow.start_time) ||
    (edits.end_time        !== undefined && edits.end_time        !== oldRow.end_time) ||
    (edits.valid_from      !== undefined && edits.valid_from      !== oldRow.valid_from) ||
    (edits.valid_until     !== undefined && edits.valid_until     !== oldRow.valid_until) ||
    (edits.interval_weeks  !== undefined && edits.interval_weeks  !== oldRow.interval_weeks) ||
    (edits.price           !== undefined && edits.price           !== oldRow.price) ||
    (edits.payment_method  !== undefined && edits.payment_method  !== oldRow.payment_method) ||
    (edits.comment !== undefined && commentNormalized(edits.comment) !== (oldRow.comment ?? null));

  if (!hasChange) {
    return { noop: true, reason: "no_changes" };
  }

  // Split-date — Beslut C: operator's chosen valid_from. Falls back to
  // `today` only if edits.valid_from was not provided (defensive — UI
  // always pre-fills the field with today's date).
  const splitDate = edits.valid_from ?? today;

  // Effective new-row values: copy old, apply edits.
  const effective = {
    court_id:         edits.court_id        ?? oldRow.court_id,
    day_of_week:      edits.day_of_week     ?? oldRow.day_of_week,
    start_time:       edits.start_time      ?? oldRow.start_time,
    end_time:         edits.end_time        ?? oldRow.end_time,
    valid_until:      edits.valid_until     ?? oldRow.valid_until,    // preserved by default
    interval_weeks:   edits.interval_weeks  ?? oldRow.interval_weeks,
    price:            edits.price           ?? oldRow.price,
    payment_method:   edits.payment_method  ?? oldRow.payment_method,
    comment:          edits.comment !== undefined
                        ? commentNormalized(edits.comment)
                        : oldRow.comment,
  };

  const newPriceMinor =
    edits.price !== undefined
      ? Math.round((effective.price ?? 0) * 100)
      : (oldRow.price_minor ?? Math.round((oldRow.price ?? 0) * 100));

  // ─── S1-fix (D12) — in-place branch ──────────────────────────────────
  // splitDate === oldRow.valid_from → no historical period to protect.
  // UPDATE existing row in place (same id) — no split, no migration.
  // RPC mirrors this same condition; client passes identical args.
  if (splitDate === oldRow.valid_from) {
    return {
      noop: false,
      mode: "inplace",
      splitDate,
      update: {
        id: oldRow.id,
        court_id: effective.court_id,
        day_of_week: effective.day_of_week,
        start_time: effective.start_time,
        end_time: effective.end_time,
        valid_until: effective.valid_until,
        interval_weeks: effective.interval_weeks,
        price: effective.price,
        price_minor: newPriceMinor,
        payment_method: effective.payment_method,
        comment: effective.comment,
      },
    };
  }
  // ─────────────────────────────────────────────────────────────────────

  // Split-branch — splitDate > oldRow.valid_from (note: splitDate <
  // oldRow.valid_from is permitted by the plan-builder but the RPC's
  // range guard will reject it).
  const splitDateMinusOne = isoDateMinusOne(splitDate);

  // Skipped-dates split around splitDate
  const skipped = oldRow.skipped_dates ?? [];
  const oldRetain = skipped.filter((d) => d < splitDate);
  const newCarry = skipped.filter((d) => d >= splitDate);

  const newInsert: Omit<SubscriptionRow, "id"> = {
    organization_id:     oldRow.organization_id,
    user_id:             oldRow.user_id,
    customer_id:         oldRow.customer_id,
    court_id:            effective.court_id,
    day_of_week:         effective.day_of_week,
    start_time:          effective.start_time,
    end_time:            effective.end_time,
    valid_from:          splitDate,                    // ← Beslut C
    valid_until:         effective.valid_until,
    status:              oldRow.status,                // ← Beslut D
    skipped_dates:       newCarry.length > 0 ? newCarry : null,
    price:               effective.price,
    price_minor:         newPriceMinor,
    currency:            oldRow.currency,
    interval_weeks:      effective.interval_weeks,
    comment:             effective.comment,
    payment_method:      effective.payment_method,
    payment_method_id:   oldRow.payment_method_id,
    created_by:          oldRow.created_by,
  };

  return {
    noop: false,
    mode: "split",
    splitDate,
    oldUpdate: {
      id: oldRow.id,
      valid_until: splitDateMinusOne,
      skipped_dates: oldRetain,
    },
    newInsert,
    releaseMigration: {
      oldId: oldRow.id,
      filterCondition: "release_date >= splitDate AND status='released'",
    },
    exceptionMigration: {
      oldId: oldRow.id,
      filterCondition: "original_date >= splitDate",
    },
    skippedDatesSplit: {
      oldRetain,
      newCarry,
    },
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────

/**
 * Subtract 1 day from an ISO "YYYY-MM-DD" date string. Pure: no TZ trickery
 * (treats the date as a plain calendar date, which is what `date`-type
 * columns in Postgres are).
 *
 * Example: "2026-05-18" → "2026-05-17"
 *          "2026-03-01" → "2026-02-28" (or 2026-02-29 in leap year)
 *          "2026-01-01" → "2025-12-31"
 */
export function isoDateMinusOne(iso: string): string {
  // Parse as UTC to avoid any local-TZ DST drift
  const [yStr, mStr, dStr] = iso.split("-");
  const y = parseInt(yStr, 10);
  const m = parseInt(mStr, 10);
  const d = parseInt(dStr, 10);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - 1);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}
